import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Typography
} from '@mui/material';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { DateRange, StateFilter, TagSelector, TimezoneFilter } from '../../../features';
import { X } from '@phosphor-icons/react';
import { AttemptedLeadFilterInput } from '@coverright/data-access/types/enrollment';

type AttemptedLeadFilter = AttemptedLeadFilterInput & { showPartnerLeads?: boolean | null };

interface AttemptedLeadsFilterDrawerProps {
  open: boolean,
  onClose: (filter?: AttemptedLeadFilter) => void,
  initial: AttemptedLeadFilter
}

interface Props {
  open: (onCloseCallback: (filter: AttemptedLeadFilter) => void, filters: AttemptedLeadFilter) => void;
}

const AttemptedLeadsFilterDrawerContext = React.createContext<Props>({
  open: () => {}
})

export function withAttemptedLeadsFilterDrawer(WrappedComponent: React.ComponentType) {
  return () => {
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState<AttemptedLeadFilter>({});
    const callback = useRef<(filter: AttemptedLeadFilter) => void>();

    const onClose: AttemptedLeadsFilterDrawerProps['onClose'] = filter => {
      setOpen(false);
      filter && callback.current && callback.current(filter);
    }

    const openDrawer = (onCloseCallback: (filter: AttemptedLeadFilter) => void, filter: AttemptedLeadFilter) => {
      setFilters(filter)
      callback.current = onCloseCallback;
      setOpen(true);
    }

    return <AttemptedLeadsFilterDrawerContext.Provider value={{open: openDrawer}}>
      <AttemptedLeadsFilterDrawer open={open} onClose={onClose} initial={filters}  />
      <WrappedComponent />
    </AttemptedLeadsFilterDrawerContext.Provider>
  }
}

export const useAttemptedLeadsFilterDrawer = () => useContext(AttemptedLeadsFilterDrawerContext)

function AttemptedLeadsFilterDrawer({ open, onClose, initial }: AttemptedLeadsFilterDrawerProps) {
  const [filters, setFilters] = useState(initial || {});

  useEffect(() => {
    if (open) {
      setFilters(initial || {});
    } else {
      setFilters({});
    }
  }, [open, initial]);

  const setFilterValues = (values: Array<{ name: keyof AttemptedLeadFilter, value: any }>) => {
    setFilters(prev => {
      const result = prev ? { ...prev } : {};
      for (const value of values) {
        result[value.name] = value.value === '-' ? undefined : value.value || undefined;
      }
      return result;
    });
  };

  return <Drawer
    anchor={'right'}
    open={open}
    onClose={() => onClose()}
  >
    <Card sx={{ p: 4, width: 500, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant={'h3'}>Attempted leads filters</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size={'small'} onClick={() => onClose()}>
            <X />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 3, mt: 1 }} />
      <Stack component={'form'} onSubmit={(e: any) => e.preventDefault()} spacing={3}>
        <DateRange value={filters.range}
                   label={'Lead date'}
                   onChange={value => setFilterValues([{ name: 'range', value }])} />
        <StateFilter value={filters.state || ''} onChange={value => setFilterValues([{ name: 'state', value }])} />
        <TimezoneFilter value={filters.timeZone || ''} onChange={value => setFilterValues([{ name: 'timeZone', value }])} />

        <TagSelector selectedValues={filters.includedTags || []} label={'Tags'}
                     onChange={value => setFilterValues([{ name: 'includedTags', value: value.map(t => t.id) }])} />

        <FormControlLabel
          control={
            <Checkbox
              checked={!!filters.iep}
              onChange={(e, checked) => setFilterValues([{ name: 'iep', value: checked }])}
              color="primary"
            />
          }
          label="Initial Enrollment Period"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={!!filters.excludeBadNumbers}
              onChange={(e, checked) => setFilterValues([{ name: 'excludeBadNumbers', value: checked }])}
              color="primary"
            />
          }
          label="Exclude Bad Numbers"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={!!filters.excludeCalledToday}
              onChange={(e, checked) => setFilterValues([{ name: 'excludeCalledToday', value: checked }])}
              color="primary"
            />
          }
          label="Exclude Called Today"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={!!filters.excludeDoNotCall}
              onChange={(e, checked) => setFilterValues([{ name: 'excludeDoNotCall', value: checked }])}
              color="primary"
            />
          }
          label="Exclude Do Not Call"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={!!filters.showPartnerLeads}
              onChange={(e, checked) => setFilterValues([{ name: 'showPartnerLeads', value: checked }])}
              color="primary"
            />
          }
          label="Show partner leads"
        />

        <div>
          <Button variant={'contained'} type={'submit'} onClick={() => onClose(filters)}>Search</Button>
        </div>
      </Stack>
    </Card>
  </Drawer>;
}
